import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Star, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function TestimonialsPage() {
  const testimonials = [
    {
      name: 'James R.',
      location: 'Westminster',
      rating: 5,
      date: 'January 2026',
      text: 'Quick, professional, and very clean work. The engineer arrived within 30 minutes of my emergency call and fixed the burst pipe efficiently. Highly recommended for anyone needing reliable plumbing services.',
      service: 'Emergency Plumbing'
    },
    {
      name: 'Sarah M.',
      location: 'Fulham',
      rating: 5,
      date: 'December 2025',
      text: 'Excellent service and fair pricing. They completely renovated our bathroom and the quality of work is outstanding. The team was respectful, punctual, and cleaned up after themselves every day. Will use again.',
      service: 'Bathroom Installation'
    },
    {
      name: 'Daniel K.',
      location: 'Lambeth',
      rating: 4.5,
      date: 'November 2025',
      text: 'Arrived on time and fixed the issue efficiently. My boiler was making strange noises and they diagnosed and repaired it quickly. The engineer explained everything clearly and gave me maintenance tips too.',
      service: 'Boiler Repair'
    },
    {
      name: 'Emma L.',
      location: 'Wandsworth',
      rating: 5,
      date: 'October 2025',
      text: 'Outstanding service from start to finish. They installed a new water heater and the whole process was smooth and professional. Great communication and attention to detail. Very happy with the results.',
      service: 'Installation'
    },
    {
      name: 'Michael T.',
      location: 'Camden',
      rating: 5,
      date: 'September 2025',
      text: 'These guys saved the day! Had a major leak on a Sunday evening and they were at my door within 45 minutes. Fixed everything perfectly and didn\'t charge extra for the weekend callout. Absolute legends.',
      service: 'Emergency Leak Repair'
    },
    {
      name: 'Lucy P.',
      location: 'Bromley',
      rating: 5,
      date: 'August 2025',
      text: 'Professional, courteous, and knowledgeable. The team replaced all the pipework in our Victorian house. They worked efficiently, kept everything clean, and the quality is exceptional. Couldn\'t ask for better.',
      service: 'Pipework Replacement'
    },
    {
      name: 'Robert H.',
      location: 'Brent',
      rating: 4.5,
      date: 'July 2025',
      text: 'Very pleased with the service. Fixed our persistent drainage issue that other plumbers couldn\'t solve. The engineer was thorough in diagnosing the problem and the repair has held up perfectly.',
      service: 'Drainage Service'
    },
    {
      name: 'Sophie W.',
      location: 'Greenwich',
      rating: 5,
      date: 'June 2025',
      text: 'Fantastic experience from booking to completion. They installed a new bathroom suite and did an amazing job. The attention to detail and craftsmanship is superb. Already recommended to friends.',
      service: 'Bathroom Installation'
    },
    {
      name: 'David C.',
      location: 'Islington',
      rating: 5,
      date: 'May 2025',
      text: 'Transparent pricing and excellent work. No hidden charges, everything was explained upfront. The engineer was friendly, professional, and completed the job to a very high standard.',
      service: 'General Plumbing'
    }
  ];

  const stats = [
    { value: '4.9/5', label: 'Average Rating' },
    { value: '5000+', label: 'Happy Customers' },
    { value: '98%', label: 'Would Recommend' },
    { value: '24/7', label: 'Support Available' }
  ];

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={i} className="w-4 h-4 fill-primary text-primary" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Star key="half" className="w-4 h-4 fill-primary/50 text-primary" />
      );
    }

    return stars;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-b from-muted/30 to-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6 mb-16 animate-slide-up">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              Customer Feedbacks
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight">
              What Our Customers{' '}
              <span className="text-primary">Say About Us</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Don\'t just take our word for it. Here\'s what London residents and businesses 
              have to say about their experience with Elite Plumbers.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {stats.map((stat, index) => (
              <Card key={index} className="p-6 text-center border-2 hover:border-primary/50 transition-all duration-300">
                <p className="text-3xl md:text-4xl font-bold font-heading text-primary mb-2">
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index}
                className="p-6 border-2 hover:border-primary/50 hover:shadow-lg transition-all duration-300 group flex flex-col"
              >
                {/* Quote Icon */}
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors duration-300">
                  <Quote className="w-5 h-5 text-primary" />
                </div>

                {/* Review Text */}
                <p className="text-muted-foreground text-sm leading-relaxed mb-4 flex-grow">
                  "{testimonial.text}"
                </p>

                {/* Service Badge */}
                <Badge variant="outline" className="w-fit mb-4 text-xs border-primary/30 text-primary">
                  {testimonial.service}
                </Badge>

                {/* Rating */}
                <div className="flex items-center gap-1 mb-3">
                  {renderStars(testimonial.rating)}
                </div>

                {/* Customer Info */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-foreground">{testimonial.name}</p>
                    <p className="text-xs text-muted-foreground">{testimonial.location}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">{testimonial.date}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Showcase */}
      <section className="py-20 bg-muted/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground">
              Transformations We're Proud Of
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Real results from real customers across London
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="overflow-hidden border-2">
              <div className="relative h-80">
                <img
                  src="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxiYXRocm9vbSUyMHJlbm92YXRpb258ZW58MHx8fHwxNzY5ODE5NzkxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Bathroom renovation completed"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 bg-card">
                <div className="flex items-center justify-between mb-3">
                  <Badge className="bg-primary/10 text-primary">Westminster Project</Badge>
                  <div className="flex items-center gap-1">
                    {renderStars(5)}
                  </div>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  Complete Bathroom Transformation
                </h3>
                <p className="text-sm text-muted-foreground">
                  "Elite Plumbers transformed our dated bathroom into a modern oasis. Exceptional work!"
                </p>
              </div>
            </Card>

            <Card className="overflow-hidden border-2">
              <div className="relative h-80">
                <img
                  src="https://images.unsplash.com/photo-1629079447777-1e605162dc8d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxiYXRocm9vbSUyMHJlbm92YXRpb258ZW58MHx8fHwxNzY5ODE5NzkxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Luxury bathroom installation"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 bg-card">
                <div className="flex items-center justify-between mb-3">
                  <Badge className="bg-primary/10 text-primary">Fulham Project</Badge>
                  <div className="flex items-center gap-1">
                    {renderStars(5)}
                  </div>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  Luxury Suite Installation
                </h3>
                <p className="text-sm text-muted-foreground">
                  "The attention to detail and craftsmanship exceeded all our expectations."
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-muted/30">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <Star className="w-8 h-8 text-primary fill-primary" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground">
            Join Thousands of Satisfied Customers
          </h2>
          <p className="text-lg text-muted-foreground">
            Experience the same level of professional service that has earned us 
            a 4.9-star rating across London.
          </p>
          <Button 
            asChild
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-glow"
          >
            <Link to="/contact">Book Your Service</Link>
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-secondary text-secondary-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold">
            Ready to Experience Elite Service?
          </h2>
          <p className="text-lg text-secondary-foreground/80">
            Get in touch today and discover why we\'re London\'s most trusted plumbing company.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-glow"
            >
              <Link to="/contact">Get a Quote</Link>
            </Button>
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-secondary-foreground/20 hover:bg-secondary-foreground/10"
            >
              <a href="tel:07356255155">Call 07356 255155</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}